/**
 * One pending tool request as a card, answered where you are (ADR 0072): the session drawer, an
 * answer popover on Overview or a mission page, the answer sheet. The same look as the question
 * card (AskCard): the verb and its target, risk at every level (RiskLabel, quiet unless high), time left, one quiet line of where
 * it comes from, the exact call, then the shared answer (ApprovalAnswer). "Open in Inbox" stays
 * in the More menu.
 */
import { Hand, Inbox } from '@orgus/ui/icons';
import { useNavigate } from 'react-router';
import type { ApprovalRequest } from '@orgus/protocol';
import { DropdownMenuItem, cn, formatRelative, useNow } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useSessionTitle } from '../sessions/title.tsx';
import { ApprovalAnswer, useDecision } from './ApprovalAnswer.tsx';
import { TimeLeft } from './AskAnswer.tsx';
import { CommandBlock } from './InboxDetail.tsx';
import { RiskLabel } from './RiskLabel.tsx';
import { shortTarget, titleParts } from './inboxModel.ts';

export interface RequestCardProps {
  a: ApprovalRequest;
  /** 'card': a bordered card (session drawer). 'bare': inside a popover or sheet that frames it. */
  variant?: 'card' | 'bare';
  /** Say which session it comes from (off in the session's own drawer). */
  showSession?: boolean;
  /** After the answer went through (a popover closes). */
  onDone?: () => void;
  className?: string;
}

export function RequestCard({ a, variant = 'card', showSession = true, onDone, className }: RequestCardProps) {
  const now = useNow(15_000);
  const navigate = useNavigate();
  const machine = useOrgus((s) => s.machines[a.machineId]?.name);
  const project = useOrgus((s) => (a.projectId ? s.projects[a.projectId]?.name : undefined));
  const session = useOrgus((s) => s.sessions[a.sessionId]);
  const titleOf = useSessionTitle();
  const c = useDecision(a, { machine, project, files: variant === 'card', onDone: () => onDone?.() });
  const p = titleParts(a);
  const where = [showSession && session ? titleOf(session).title : null, project ?? machine, `asked ${formatRelative(a.createdAt, now)}`].filter(Boolean).join('  ·  ');
  return (
    <section aria-label={a.title} data-testid="request-card" className={cn('flex min-w-0 flex-col gap-4', variant === 'card' && 'rounded-card border border-border bg-surface p-4', className)}>
      <header className="flex min-w-0 items-start gap-3">
        <span className="flex h-5 shrink-0 items-center">
          <Hand className="size-4 text-status-attention" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className="flex min-w-0 flex-1 items-baseline gap-2 text-ui font-medium text-fg">
              <span className="shrink-0">{p.verb}</span>
              {p.target && (
                <span className="min-w-0 truncate font-mono text-caption font-normal text-fg-muted" title={p.full}>
                  {shortTarget(p, 44)}
                </span>
              )}
            </span>
            <RiskLabel risk={a.risk} />
            <TimeLeft a={a} now={now} />
          </span>
          <span className="truncate text-caption text-fg-subtle num">{where}</span>
        </span>
      </header>
      <CommandBlock approval={a} compact />
      {a.policyHint?.reason && <p className="text-caption text-fg-subtle">{a.policyHint.reason}</p>}
      <ApprovalAnswer
        c={c}
        layout="compact"
        menu={
          <DropdownMenuItem icon={<Inbox />} onSelect={() => navigate(`/inbox?approval=${encodeURIComponent(a.id)}`)}>
            Open in Inbox
          </DropdownMenuItem>
        }
      />
      {c.dialog}
    </section>
  );
}
